/**
 * T2744: valute v svojem modulu, da jih lahko bere `ProjectCurrencyContext`,
 * ne da bi ga podrl `vi.mock('@/lib/formatters')` v ~70 testih (ti
 * formatirnik zamenjajo z zgolj `formatNumber`). `formatters.ts` jih izvozi
 * naprej, zato obstoječi uvozi ostanejo nespremenjeni.
 */

/**
 * T1298: simbol valute iz ISO kode — zrcalo `apps/core/formats.py`.
 *
 * Zakaj obstaja: nadzorna plošča je zneske izpisovala s KODO
 * (»1.234,00 EUR«), njen lastni PDF in Excel pa s simbolom (»1.234,00 €«),
 * tako kot preostanek aplikacije. Ista številka v dveh zapisih. Neznana koda
 * se izpiše taka, kot je — bolje koda kot napačen simbol.
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  CHF: 'CHF',
  HRK: 'kn',
  RSD: 'din',
  // T2745: CAD je »CA$«, ne »$« — sicer bi bila projekt v CAD in projekt v USD
  // zapisana enako (zrcalo `apps/core/formats.py`). DKK in SEK sta oba »kr«.
  CAD: 'CA$',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  DKK: 'kr',
  SEK: 'kr',
  RON: 'lei',
};

export const currencySymbol = (currency: string | null | undefined): string =>
  CURRENCY_SYMBOLS[(currency ?? '').toUpperCase()] ?? currency ?? '';

/**
 * T2733: valute, ki jih ponuja polje »Valuta« v obrazcu projekta — zrcalo
 * `PROJECT_CURRENCIES` v `apps/core/formats.py` (zaledje drugih ne sprejme).
 * EUR je prvi in privzet (odločitev Alena 16.10.2026). Brez HRK: Hrvaška je
 * 1.1.2023 prevzela evro.
 *
 * T2745 (odločitev Alena 16.10.2026): + CAD in valute EU brez evra. Brez BGN —
 * Bolgarija je 1.1.2026 prevzela evro. Isti seznam ponuja izbirnik valute
 * organizacije (`OrgTab`).
 */
export const PROJECT_CURRENCIES = [
  'EUR', 'USD', 'GBP', 'CHF', 'RSD',
  'CAD', 'PLN', 'CZK', 'HUF', 'DKK', 'SEK', 'RON',
] as const;
export const DEFAULT_PROJECT_CURRENCY = 'EUR';

/**
 * T2060 (odločitev Alena, 26.9.2026): tržnica je SAMO v EUR — model cen nima
 * stolpca za valuto (oznake polj so »Cena (EUR)«), isto pravilo velja za pošto
 * (`apps/marketplace/services/notify.py`, `MARKETPLACE_CURRENCY`).
 * T2752: en vir za `useOrgFormats` in `SurveyorFacts` (»€/h«) namesto dveh
 * trdih »€«. Ko modul dobi več valut, se popravi tukaj.
 */
export const MARKETPLACE_CURRENCY = 'EUR';

/**
 * T2756 (odločitev Alena 16.10.2026): cene v knjižnici so v **valuti
 * organizacije**, razen sistemskih postavk (`organization === null`, npr.
 * katalog TSC), ki so v EUR. `LibraryItem.reference_price` valute nima — je
 * gola številka, ki jo je vnesla organizacija v svoji valuti, sistemski
 * katalog pa je zapisan v evrih.
 */
export const SYSTEM_LIBRARY_CURRENCY = 'EUR';

/**
 * Valuta cene knjižnične postavke.
 *
 * Brez postavke (nov vnos, uvoz v lastno knjižnico, shranjevanje iz popisa)
 * je to valuta organizacije — tja se cena zapiše. Samo izrecni `null` pomeni
 * sistemsko postavko; `undefined` (postavka brez polja) ne.
 */
export function libraryItemCurrency(
  item: { organization?: number | null } | null | undefined,
  orgCurrency: string | null | undefined,
): string {
  if (item && item.organization === null) return SYSTEM_LIBRARY_CURRENCY;
  return (typeof orgCurrency === 'string' && orgCurrency) || DEFAULT_PROJECT_CURRENCY;
}

/**
 * T2758: izid primerjave valute knjižničnih cen z valuto projekta.
 *
 * `source` so vse valute virov, ki se od cilja razlikujejo (urejene, ločene z
 * vejico — izbor lahko meša postavke organizacije in sistemske v EUR),
 * `count` pa število postavk v drugi valuti.
 */
export interface LibraryCurrencyMismatch {
  source: string;
  target: string;
  count: number;
}

/**
 * T2758: ali gre cena knjižnične postavke v projekt v drugi valuti.
 *
 * Tečajev ni (odločitev Alena 14.10.2026, T2671): zaledje prenese
 * `reference_price` v `unit_price` kot golo številko
 * (`apps/library/services/boq_transfer.py`), zato odjemalec ne preračunava,
 * ampak le pove. `null` pomeni »enaka valuta, brez obvestila«.
 */
export function libraryCurrencyMismatch(
  owners: ReadonlyArray<{ organization?: number | null } | null | undefined>,
  orgCurrency: string | null | undefined,
  projectCurrency: string | null | undefined,
): LibraryCurrencyMismatch | null {
  const target = (projectCurrency || DEFAULT_PROJECT_CURRENCY).toUpperCase();
  const sources = new Set<string>();
  let count = 0;
  for (const owner of owners) {
    const source = libraryItemCurrency(owner, orgCurrency).toUpperCase();
    if (source === target) continue;
    sources.add(source);
    count += 1;
  }
  if (count === 0) return null;
  return { source: [...sources].sort().join(', '), target, count };
}
